/**
 * Page Options - toggles on/off pour chaque effet visuel.
 */

import { type EffectsPrefs, loadEffectsPrefs, saveEffectsPrefs } from '@state/effects-prefs';
import { html, nothing } from 'lit';
import { deactivateAlBhed, isAlBhedActive } from '../effects/konami';

interface EffectOption {
  key: keyof EffectsPrefs;
  label: string;
  description: string;
}

const EFFECTS: EffectOption[] = [
  { key: 'particles', label: 'Particules', description: 'Etincelles dorees flottantes' },
  { key: 'shader', label: 'Shader Aurora', description: 'Fond anime WebGL dore' },
  { key: 'metaballs', label: 'Metaballs', description: 'Blobs organiques en arriere-plan' },
  { key: 'tilt', label: 'Tilt 3D', description: 'Cards qui pivotent au survol' },
  { key: 'cursorGlow', label: 'Halo Curseur', description: 'Lueur doree suivant la souris' },
  { key: 'runes', label: 'Runes Flottantes', description: 'Symboles magiques en fond' },
  { key: 'portal', label: 'Portal Entree', description: 'Portes 3D au chargement' },
  {
    key: 'pageTransitions',
    label: 'Transitions Pages',
    description: 'Effets aleatoires entre pages',
  },
  { key: 'gyroscope', label: 'Gyroscope', description: 'Parallax en inclinant le telephone' },
  {
    key: 'magnetic',
    label: 'Attraction Magnetique',
    description: 'Elements qui attirent le curseur',
  },
  { key: 'morphText', label: 'Texte Runes', description: 'Titre decode depuis des runes' },
  { key: 'typing', label: 'Machine a Ecrire', description: 'Sous-titre tape lettre par lettre' },
  { key: 'counters', label: 'Compteurs Animes', description: 'Chiffres qui comptent de 0' },
];

export function renderOptionsPage(onStateChange: () => void): unknown {
  const prefs = loadEffectsPrefs();

  function toggle(key: keyof EffectsPrefs) {
    prefs[key] = !prefs[key];
    saveEffectsPrefs(prefs);
    onStateChange();
  }

  function enableAll() {
    for (const e of EFFECTS) prefs[e.key] = true;
    saveEffectsPrefs(prefs);
    onStateChange();
  }

  function disableAll() {
    for (const e of EFFECTS) prefs[e.key] = false;
    saveEffectsPrefs(prefs);
    onStateChange();
  }

  const enabledCount = EFFECTS.filter((e) => prefs[e.key]).length;

  return html`
    <div class="options-page">
      <header class="options-header">
        <h1 class="options-title">Effets Visuels</h1>
        <p class="options-subtitle">${enabledCount}/${EFFECTS.length} actifs. Les changements s'appliquent au rechargement.</p>
        <div class="options-bulk">
          <button class="options-bulk-btn" @click=${enableAll}>Tout activer</button>
          <button class="options-bulk-btn" @click=${disableAll}>Tout desactiver</button>
        </div>
      </header>
      <div class="options-list">
        ${EFFECTS.map(
          (e) => html`
          <div class="options-item">
            <div class="options-item-info">
              <span class="options-item-label">${e.label}</span>
              <span class="options-item-desc">${e.description}</span>
            </div>
            <button
              class="options-toggle ${prefs[e.key] ? 'on' : 'off'}"
              @click=${() => toggle(e.key)}
              aria-pressed=${prefs[e.key]}
              aria-label="${e.label} ${prefs[e.key] ? 'actif' : 'inactif'}"
            >
              <span class="options-toggle-knob"></span>
            </button>
          </div>
        `,
        )}
      </div>
      ${
        isAlBhedActive()
          ? html`
        <div class="options-albhed">
          <div class="options-item" style="border-color: rgba(100, 220, 255, 0.3); margin-top: var(--space-4);">
            <div class="options-item-info">
              <span class="options-item-label" style="color: rgb(100, 220, 255);">Al Bhed</span>
              <span class="options-item-desc">Langue alternative (Konami code)</span>
            </div>
            <button
              class="options-toggle on"
              style="background: rgb(100, 220, 255);"
              @click=${() => deactivateAlBhed()}
              aria-pressed="true"
              aria-label="Al Bhed actif"
            >
              <span class="options-toggle-knob"></span>
            </button>
          </div>
        </div>
      `
          : nothing
      }
    </div>
  `;
}
